import {Chip} from '@dropbox/dig-components/chip';
import {LayerContext} from '@dropbox/dig-components/layer';
import {Menu} from '@dropbox/dig-components/menu';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, DropboxDashLine, LibraryLine} from '@dropbox/dig-icons/assets';
import {DropboxOSLine} from 'components/DSYS/DropboxOSLine';
import {StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {CurrentEnvironment, getCurrentEnvironment} from 'helpers/environment';
import {useRef, useState} from 'react';
import {AUTODRAFT_APPS, AutodraftAppIcon} from 'views/workstreams/autodraftApps';
import {useDashConnections} from 'views/workstreams/useDashConnections';

const DROPBOXOS_CAPABILITIES = [
  'Workstreams',
  'Outcomes',
  'People',
  'Teams',
  'News',
  'Gratitude',
  'Badges',
];

const nonInteractive = {pointerEvents: 'none', cursor: 'default'} as const;

const DashChip = () => {
  const {data: connectionMap} = useDashConnections();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const sourceLabels = connectionMap
    ? AUTODRAFT_APPS.filter((a) => connectionMap.has(a.key) && a.key !== 'dropboxos')
        .map((a) => a.label)
        .join(' · ')
    : '';

  const openDashSettings = () => {
    const env = getCurrentEnvironment();
    const dashUrl =
      env === CurrentEnvironment.Production
        ? 'https://dash.ai/settings/apps'
        : 'https://staging.dash.ai/settings/apps';
    window.open(dashUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Tooltip.Control
        open={hovering && !menuOpen}
        triggerRef={triggerRef}
        placement="bottom"
        openDelay={200}
      >
        {'Connected sources: ' + (sourceLabels || 'Dash')}
      </Tooltip.Control>
      <span
        ref={triggerRef}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <Menu.Wrapper
          shouldPropagateClickOutsideMouseEvents
          onToggle={({isOpen}) => setMenuOpen(isOpen)}
        >
          {({getContentProps, getTriggerProps}) => (
            <>
              <Chip {...getTriggerProps()} size="small">
                <Chip.IconAccessory>
                  <UIIcon src={DropboxDashLine} size="small" />
                </Chip.IconAccessory>
                <Chip.Content>
                  <Box display="flex" alignItems="center" style={{gap: 6}}>
                    Dash
                    <StatusButtonIcon size="xsmall" status="on_track" />
                  </Box>
                </Chip.Content>
                <Chip.IconAccessory>
                  <UIIcon src={ChevronDownLine} size="small" />
                </Chip.IconAccessory>
              </Chip>
              <Menu.Content {...getContentProps()} placement="bottom-end" minWidth="200px">
                <Menu.Segment>
                  {AUTODRAFT_APPS.filter((app) => app.key !== 'dropboxos').map((app) => {
                    const isConnected = connectionMap?.get(app.key) === 'connected';
                    return (
                      <Menu.ActionItem
                        key={app.key}
                        withLeftAccessory={<AutodraftAppIcon app={app} size={16} />}
                        withRightAccessory={
                          <StatusButtonIcon
                            size="xsmall"
                            status={isConnected ? 'on_track' : 'missed'}
                          />
                        }
                        onClick={openDashSettings}
                      >
                        {app.label}
                      </Menu.ActionItem>
                    );
                  })}
                </Menu.Segment>
              </Menu.Content>
            </>
          )}
        </Menu.Wrapper>
      </span>
    </>
  );
};

export const AiSearchConnectedSources = ({tooltipZIndex}: {tooltipZIndex?: number}) => {
  const connectedSources = (
    <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap: 6}} marginLeft="12">
      <Tooltip
        title={'Connected sources: ' + DROPBOXOS_CAPABILITIES.join(' · ')}
        placement="bottom"
        openDelay={200}
      >
        <span role="presentation">
          <Chip size="small" style={nonInteractive} tabIndex={-1}>
            <Chip.IconAccessory>
              <DropboxOSLine width={16} height={16} />
            </Chip.IconAccessory>
            <Chip.Content>
              <Box display="flex" alignItems="center" style={{gap: 6}}>
                CompanyOS
                <StatusButtonIcon size="xsmall" status="on_track" />
              </Box>
            </Chip.Content>
          </Chip>
        </span>
      </Tooltip>
      <Tooltip title="Connected source: Knowledge articles" placement="bottom" openDelay={200}>
        <span>
          <Chip size="small" style={nonInteractive} tabIndex={-1}>
            <Chip.IconAccessory>
              <UIIcon src={LibraryLine} size="small" />
            </Chip.IconAccessory>
            <Chip.Content>
              <Box display="flex" alignItems="center" style={{gap: 6}}>
                ServiceNow
                <StatusButtonIcon size="xsmall" status="on_track" />
              </Box>
            </Chip.Content>
          </Chip>
        </span>
      </Tooltip>
      <DashChip />
    </Box>
  );

  return tooltipZIndex === undefined ? (
    connectedSources
  ) : (
    <LayerContext.Provider value={{zIndex: tooltipZIndex}}>
      {connectedSources}
    </LayerContext.Provider>
  );
};
